import { resolveOrgAnalyticRules, resolveOrgAnalyticValue } from '../assistant/orgAnalyticLensRules';
import { resolveColorLensRules } from '../../../core/assistant/utils/colorLensRules';
import { buildStructuralFields } from '../services/orgChartStructuralFields';
import { useCallback, useMemo } from 'react';
import {
  useViewRulesStore,
  stripBlockedDisplayFields,
} from '../../../core/assistant/stores/viewRulesStore';
import { useDiagramStore } from '../../../stores/diagramStore';
import { useOrgChartViewStore } from '../../../stores/orgchartViewStore';
import { evaluateConditions, NodeData, resolveNodeField, resolveTemplate } from '../../../core/assistant/utils/conditionEvaluation';
import type {
  ViewRule,
  ComputedBadge,
  ComputedLabel,
  BadgePosition,
  FilterEffect,
} from '../../../core/assistant/models/ViewRules';
import {
  PRIMARY_FILTER_ID,
  getRuleKind,
  getActiveFilterRules,
  getEffectiveFilterEffect,
  getEffectiveShowReportingLines,
} from '../../../core/assistant/models/ViewRules';
import type { Edge, Node } from '../../../core/models/scene';
import type { ShapeDataBinding } from '../../../domains/data-binding/models/ShapeDataBinding';
import { normalizeFieldId } from '../fields/import-aliases';
import { useDiagramDocument } from '../../../contexts/DiagramDocumentContext';
import { useDiagramSettings } from '../../../hooks/useDiagramSettings';
import { useDiagramInfo } from '../../../hooks/useDiagramInfo';
import { resolveNodeCustomFields } from '../components/orgCardFields';
import { buildOrgAssistantDiscoveryNodes } from '../assistant/orgAssistantFieldDiscovery';
import { useFolderRelationships } from '../../data-binding/hooks/useFolderSourceBindings';
import {
  ORG_WORKLOAD_LENS_FIELDS,
  OrgWorkloadLensFieldService,
} from '../services/OrgWorkloadLensFieldService';
import { useScenariosStore } from '../../../core/assistant/stores/scenariosStore';
import { buildSuccessionComputedFieldMap } from '../services/SuccessionPlanningService';
import { isSuccessionLensActive } from '../assistant/successionLens';
import { resolveSuccessionRiskDisplayColor } from '../assistant/successionRiskPalette';
import {
  CHAIN_BACKGROUND_COLOR,
  CHAIN_BORDER_COLOR,
  CHAIN_OPACITY,
  CHAIN_TEXT_COLOR,
  isReportingChainContextNode,
} from '../services/orgChartReportingChainDim';

const DIM_OPACITY = 0.3;
const MAX_TENURE_COLOR_GROUPS = 6;
const TENURE_BUCKET_WEIGHTS = [1, 1, 2, 3, 5, 8];
const WORKLOAD_RELATIONSHIP_TYPES = ['project_assignment', 'capacity_commitment'] as const;
const WORKLOAD_LENS_FIELD_IDS = new Set<string>(ORG_WORKLOAD_LENS_FIELDS);

const toRuleNodeData = (
  node: Node,
  computedFields?: Record<string, unknown>,
): NodeData => ({
  ...(node as unknown as Record<string, unknown>),
  id: node.id,
  name: node.name,
  attrs: {
    ...((node.attrs ?? {}) as Record<string, unknown>),
    ...(computedFields ?? {}),
  },
  props: {
    ...((node.props ?? {}) as Record<string, unknown>),
    ...(computedFields ?? {}),
  },
});

/**
 * Interpolate between two hex colors
 */
function interpolateColor(color1: string, color2: string, ratio: number): string {
  const hex1 = color1.replace('#', '');
  const hex2 = color2.replace('#', '');

  const r1 = parseInt(hex1.substring(0, 2), 16);
  const g1 = parseInt(hex1.substring(2, 4), 16);
  const b1 = parseInt(hex1.substring(4, 6), 16);

  const r2 = parseInt(hex2.substring(0, 2), 16);
  const g2 = parseInt(hex2.substring(2, 4), 16);
  const b2 = parseInt(hex2.substring(4, 6), 16);

  const r = Math.round(r1 + (r2 - r1) * ratio);
  const g = Math.round(g1 + (g2 - g1) * ratio);
  const b = Math.round(b1 + (b2 - b1) * ratio);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export interface ComputedNodeStyle {
  filterOpacity: number;
  filterVisible: boolean;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  badges: ComputedBadge[];
  labels: ComputedLabel[];
}

export interface ColorByLegendEntry {
  value: string;
  color: string;
  count: number;
}

export interface HeatmapLegendData {
  field: string;
  minColor: string;
  maxColor: string;
  minValue: number;
  maxValue: number;
}

/**
 * The colours a colour-by / heatmap lens settled on for one set of nodes,
 * reusable for another. The canvas renders only the cards left after
 * collapsing, but takes its palette from the whole chart (as the legend and
 * export do), so collapsing a branch never recolours the people on screen.
 */
export interface LensPalette {
  /** Keyed by the resolved field value (tenure: whole years). */
  colorBy: { field: string; colorsByKey: ReadonlyMap<string, string> } | null;
  heatmap: { field: string; min: number; max: number } | null;
}

export interface UseViewRulesOptions {
  /** Palette to colour with instead of one derived from `sceneNodes`. */
  lensPalette?: LensPalette | null;
}

export interface UseViewRulesResult {
  nodeStyleMap: Map<string, ComputedNodeStyle>;
  isFilterActive: boolean;
  filterMode: FilterEffect;
  matchingCount: number;
  matchingNodeIds: Set<string>;
  showReportingLines: boolean;
  reportingChainNodeIds: Set<string>;
  reportingChainEdgeIds: Set<string>;
  hasDisplayRules: boolean;
  getNodeStyle: (nodeId: string) => ComputedNodeStyle;
  isNodeVisible: (nodeId: string) => boolean;
  getNodeOpacity: (nodeId: string) => number;
  isEdgeInReportingChain: (edgeId: string) => boolean;
  getDisplayRuleStyle: (nodeId: string) => Pick<ComputedNodeStyle, 'backgroundColor' | 'borderColor' | 'textColor' | 'badges' | 'labels'>;
  /** Returns a color for a specific field on a node if it's targeted by a heatmap or colorBy rule */
  getFieldColor: (nodeId: string, fieldId: string) => string | undefined;
  /** The field ID targeted by an active heatmap rule, if any */
  activeHeatmapField: string | undefined;
  /** The field ID targeted by an active colorBy rule, if any */
  activeColorByField: string | undefined;
  /** Friendly rule name for the active colorBy rule (preferred over the raw field id when present). */
  activeColorByDisplayName: string | undefined;
  /** Friendly rule name for the active heatmap rule. */
  activeHeatmapDisplayName: string | undefined;
  /** Legend entries for the active colorBy rule */
  colorByLegend: ColorByLegendEntry[] | null;
  /** Legend data for the active heatmap rule */
  heatmapLegend: HeatmapLegendData | null;
  /** The palette derived from this hook's own nodes. */
  lensPalette: LensPalette;
  /** Number of nodes with uncommitted binding changes (always-on diff indicator) */
  bindingDiffCount: number;
}

/**
 * Evaluate if a display rule matches a node
 */
function evaluateDisplayRule(nodeData: NodeData, rule: ViewRule, isFilterMatch: boolean): boolean {
  if (!rule.enabled) return false;
  const kind = getRuleKind(rule);

  // Badge/highlight can follow active filter scope at lens level.
  if ((kind === 'badge' || kind === 'highlight') && (rule.display.scopeMode ?? 'filter') === 'filter') {
    if (!isFilterMatch) return false;
  }

  if (rule.conditions.length === 0) return true;

  return evaluateConditions(nodeData, rule.conditions, rule.logic);
}

/**
 * Compute display rule styles for a node
 */
function computeDisplayRuleStyle(
  node: Node,
  displayRules: ViewRule[],
  matchingNodeIds: Set<string>,
  secondaryReportsToByNodeId?: Map<string, string>,
  computedFieldsByNodeId?: Map<string, Record<string, unknown>>,
): Pick<ComputedNodeStyle, 'backgroundColor' | 'borderColor' | 'textColor' | 'badges' | 'labels'> {
  let backgroundColor: string | undefined;
  let borderColor: string | undefined;
  let textColor: string | undefined;
  const badges: ComputedBadge[] = [];
  const labels: ComputedLabel[] = [];
  const usedBadgePositions = new Set<BadgePosition>();

  const nodeDataBase = toRuleNodeData(node, computedFieldsByNodeId?.get(node.id));
  const secondaryManagerId = secondaryReportsToByNodeId?.get(node.id);
  const nodeData: NodeData = secondaryManagerId
    ? {
        ...nodeDataBase,
        attrs: {
          ...(nodeDataBase.attrs ?? {}),
          secondaryReportsTo: secondaryManagerId,
        },
        props: {
          ...(nodeDataBase.props ?? {}),
          secondaryReportsTo: secondaryManagerId,
        },
      }
    : nodeDataBase;

  for (const rule of displayRules) {
    if (!evaluateDisplayRule(nodeData, rule, matchingNodeIds.has(node.id))) continue;

    // Color rule
    if (rule.display.colors) {
      if (rule.display.colors.backgroundColor) backgroundColor = rule.display.colors.backgroundColor;
      if (rule.display.colors.borderColor) borderColor = rule.display.colors.borderColor;
      if (rule.display.colors.textColor) textColor = rule.display.colors.textColor;
    }

    // Badge rule
    if (rule.display.badge) {
      let position = rule.display.badge.position;
      if (usedBadgePositions.has(position)) {
        const positions: BadgePosition[] = ['top-right', 'bottom-right', 'bottom-left', 'top-left'];
        const currentIndex = positions.indexOf(position);
        for (let i = 1; i <= 4; i++) {
          const nextPosition = positions[(currentIndex + i) % 4];
          if (!usedBadgePositions.has(nextPosition)) {
            position = nextPosition;
            break;
          }
        }
      }
      usedBadgePositions.add(position);
      badges.push({
        icon: rule.display.badge.icon,
        color: rule.display.badge.color,
        position,
        tooltip: rule.display.badge.tooltip,
      });
    }

    // Label rule
    if (rule.display.label) {
      const text = resolveTemplate(rule.display.label.template, nodeData).trim();
      if (!text) continue;
      labels.push({
        text,
        position: rule.display.label.position,
        style: rule.display.label.style,
      });
    }
  }

  return {
    backgroundColor,
    borderColor,
    textColor,
    badges,
    labels,
  };
}

function ruleReferencesWorkloadLensField(rule: ViewRule): boolean {
  const fields = [
    ...rule.conditions.map((condition) => condition.field),
    rule.display.heatmap?.field,
    rule.display.colorByField?.field,
  ].filter((field): field is string => !!field);

  if (fields.some((field) => WORKLOAD_LENS_FIELD_IDS.has(field))) return true;

  const template = rule.display.label?.template ?? '';
  for (const match of template.matchAll(/\{\{(\w+)\}\}/g)) {
    if (WORKLOAD_LENS_FIELD_IDS.has(match[1])) return true;
  }

  return false;
}

function folderIdFromRoute(): string | null {
  if (typeof window === 'undefined') return null;
  return window.location.pathname.match(/\/f\/([^/]+)(?:\/|$)/)?.[1] ?? null;
}

function toWorkloadLensNode(node: Node) {
  return {
    id: node.id,
    name: node.name,
    attrs: (node.attrs ?? {}) as Record<string, unknown>,
    props: (node.props ?? {}) as Record<string, unknown>,
  };
}

/**
 * Central hook for computing view rules (filter effects + display rules) for
 * org chart and genogram diagrams.
 *
 * For unsupported diagram types, it returns safe defaults.
 */
export const useViewRules = (
  sceneNodes?: Node[],
  sceneEdges?: Edge[],
  options?: UseViewRulesOptions,
): UseViewRulesResult => {
  const { doc, diagramId } = useDiagramDocument();
  const { read: readSettings } = useDiagramSettings(doc);
  const asOfDate = useOrgChartViewStore((s) => s.asOfDate);
  const workloadLensSettings = useOrgChartViewStore((s) => s.workloadLensSettings);

  // Get state from unified store. Rules pass through the per-viewer eligibility
  // filter first: a chart whose default lens is keyed on a field this viewer
  // has no grant for renders as a plain chart, rather than an empty heatmap
  // that names the field in its legend.
  const storedRules = useViewRulesStore((s) => s.rules);
  const blockedDisplayFields = useViewRulesStore((s) => s.blockedDisplayFields);
  const rules = useMemo(
    () => resolveColorLensRules(resolveOrgAnalyticRules(stripBlockedDisplayFields(storedRules, blockedDisplayFields))),
    [storedRules, blockedDisplayFields],
  );
  const matchingNodeIds = useViewRulesStore((s) => s.matchingNodeIds);
  const reportingChainNodeIds = useViewRulesStore((s) => s.reportingChainNodeIds);
  const reportingChainEdgeIds = useViewRulesStore((s) => s.reportingChainEdgeIds);
  const storeNodes = useDiagramStore((s) => s.nodes);
  const storeEdges = useDiagramStore((s) => s.edges);
  const nodes = sceneNodes ?? storeNodes;
  const edges = sceneEdges ?? storeEdges;
  const activeScenarioId = useScenariosStore((state) => state.activeScenarioId);
  const scenarios = useScenariosStore((state) => state.scenarios);
  const activeScenario = useMemo(
    () => scenarios.find((scenario) => scenario.id === activeScenarioId) ?? null,
    [activeScenarioId, scenarios],
  );

  // Check if this diagram supports view-rules rendering. Used for
  // orgchart/genogram-specific selection-lens behaviours (reporting chains,
  // structural fields). Lens computation itself is generic — see
  // displayRuleStyleMap below.
  const isSupportedDiagram = useMemo(() => {
    return nodes.some((node) => {
      const category = (node as Node & { category?: string }).category;
      const type = node.type;
      const isOrgNode =
        category === 'orgchart' ||
        type?.startsWith('org_') ||
        type === 'org-shape' ||
        type === 'org_person' ||
        type === 'org_position' ||
        type === 'org_department';
      const isGenogramNode = category === 'genogram' || type === 'person';
      return isOrgNode || isGenogramNode;
    });
  }, [nodes]);
  void isSupportedDiagram;

  // Primary filter is still used as a legacy source for special display modes.
  const primaryFilter = rules.find((r) => r.id === PRIMARY_FILTER_ID);
  const activeFilterRules = useMemo(() => getActiveFilterRules(rules), [rules]);
  const filterEffect = useMemo(() => getEffectiveFilterEffect(rules), [rules]);
  const showReportingLines = useMemo(
    () => getEffectiveShowReportingLines(rules),
    [rules]
  );
  const isFilterActive = activeFilterRules.length > 0;

  // Get display options from primary filter (applies to matching nodes)
  const filterHighlightColors = primaryFilter?.display.colors;
  const filterBadge = primaryFilter?.display.badge;
  // Get display rules (exclude primary filter)
  const displayRules = rules.filter((r) => r.id !== PRIMARY_FILTER_ID).sort((a, b) => a.order - b.order);
  const shouldLoadWorkloadLensData = useMemo(
    () => rules.some((rule) => rule.enabled && ruleReferencesWorkloadLensField(rule)),
    [rules],
  );
  const routeFolderId = useMemo(() => folderIdFromRoute(), []);
  const needsDiagramInfoForWorkload =
    shouldLoadWorkloadLensData && !routeFolderId && Boolean(diagramId);
  const diagramInfoQuery = useDiagramInfo(
    needsDiagramInfoForWorkload ? diagramId ?? undefined : undefined,
  );
  const workloadFolderId = routeFolderId ?? diagramInfoQuery.data?.folderId ?? null;
  const workloadRelationshipsQuery = useFolderRelationships(workloadFolderId ?? undefined, {
    relationshipTypes: WORKLOAD_RELATIONSHIP_TYPES,
    limit: 1000,
    resolveIdentities: true,
    enabled: shouldLoadWorkloadLensData && Boolean(workloadFolderId),
  });
  const workloadRelationshipRecords = useMemo(
    () => workloadRelationshipsQuery.data?.records ?? [],
    [workloadRelationshipsQuery.data?.records],
  );
  const workloadLensFieldService = useMemo(() => new OrgWorkloadLensFieldService(), []);
  const workloadLensFieldsByNodeId = useMemo(
    () => shouldLoadWorkloadLensData
      ? workloadLensFieldService.buildFieldMap(
          nodes.map(toWorkloadLensNode),
          workloadRelationshipRecords,
          {
            asOf: asOfDate ?? undefined,
            workloadLensSettings,
          },
        )
      : new Map<string, Record<string, unknown>>(),
    [
      asOfDate,
      nodes,
      shouldLoadWorkloadLensData,
      workloadLensSettings,
      workloadLensFieldService,
      workloadRelationshipRecords,
    ],
  );
  const successionLensFieldsByNodeId = useMemo(
    () => isSuccessionLensActive(rules)
      ? buildSuccessionComputedFieldMap(nodes, edges, activeScenario?.delta)
      : new Map<string, Record<string, unknown>>(),
    [activeScenario?.delta, edges, nodes, rules],
  );
  const structuralFields = useMemo(() => buildStructuralFields(nodes, edges), [nodes, edges]);
  const computedFieldsByNodeId = useMemo(() => {
    const result = new Map<string, Record<string, unknown>>();
    for (const node of nodes) {
      const workload = workloadLensFieldsByNodeId.get(node.id);
      const succession = successionLensFieldsByNodeId.get(node.id);
      const directReports = structuralFields.directReportsMap.get(node.id);
      const totalReports = structuralFields.totalReportsMap.get(node.id);
      result.set(node.id, {
        ...workload, ...succession,
        ...(directReports != null ? { directReports, totalReports } : {}),
        level: structuralFields.levelMap.get(node.id),
      });
    }
    return result;
  }, [nodes, successionLensFieldsByNodeId, workloadLensFieldsByNodeId, structuralFields]);

  // Heatmap + colorByField: check primary filter first, then fall back to standalone rules.
  // Track the source rule alongside so the legend can read its display name.
  const heatmapRule = primaryFilter?.display.heatmap
    ? primaryFilter
    : displayRules.find((r) => r.enabled && r.display.heatmap);
  const colorByRule = primaryFilter?.display.colorByField
    ? primaryFilter
    : displayRules.find((r) => r.enabled && r.display.colorByField);
  const filterHeatmap = heatmapRule?.display.heatmap;
  const filterColorByField = colorByRule?.display.colorByField;
  const shouldUseNormalizedFieldResolution = Boolean(filterHeatmap || filterColorByField);

  // Check if we have a view-all mode (heatmap or colorByField) active - these apply to all nodes
  const hasViewAllMode = Boolean(filterHeatmap || filterColorByField);
  const hasDisplayRules = displayRules.length > 0 && displayRules.some((r) => r.enabled);
  const secondaryReportsToByNodeId = useMemo(() => {
    const map = new Map<string, string>();
    for (const edge of edges) {
      const edgeType = String(edge.type ?? '');
      const relation = String((edge.attrs as Record<string, unknown> | undefined)?.relation ?? '');
      const isSecondary =
        edgeType === 'dotted' ||
        edgeType === 'matrix' ||
        relation === 'dotted' ||
        relation === 'matrix' ||
        relation === 'dotted_line_reports' ||
        relation === 'matrix_reports';
      if (!isSecondary) continue;
      map.set(edge.from, edge.to);
    }
    return map;
  }, [edges]);

  // Compute display rule styles for all nodes (memoized).
  // The early return used to bail when `!isOrgChart` so non-org diagrams
  // never received badge / label / filter overlays from the lens tools.
  // Network and other domains now compute display-rule styles too — the
  // resolver below uses generic `node.attrs` / `node.props` lookup via
  // resolveNodeField, so it works for any diagram type.
  const displayRuleStyleMap = useMemo(() => {
    const map = new Map<string, Pick<ComputedNodeStyle, 'backgroundColor' | 'borderColor' | 'textColor' | 'badges' | 'labels'>>();

    if (!hasDisplayRules) {
      return map;
    }

    for (const node of nodes) {
      const style = computeDisplayRuleStyle(
        node,
        displayRules,
        matchingNodeIds,
        secondaryReportsToByNodeId,
        computedFieldsByNodeId,
      );
      if (
        style.backgroundColor ||
        style.borderColor ||
        style.textColor ||
        style.badges.length > 0 ||
        style.labels.length > 0
      ) {
        map.set(node.id, style);
      }
    }

    return map;
  }, [
    isSupportedDiagram,
    hasDisplayRules,
    nodes,
    displayRules,
    matchingNodeIds,
    secondaryReportsToByNodeId,
    computedFieldsByNodeId,
  ]);

  // Compute the style map based on filter state
  const nodeStyleMap = useMemo(() => {
    const map = new Map<string, ComputedNodeStyle>();

    if (!isFilterActive) {
      return map;
    }

    matchingNodeIds.forEach((nodeId) => {
      map.set(nodeId, {
        filterOpacity: 1,
        filterVisible: true,
        badges: [],
        labels: [],
      });
    });

    return map;
  }, [isFilterActive, matchingNodeIds]);

  const customFieldDefinitions = useMemo(() => {
    if (!shouldUseNormalizedFieldResolution) return [];
    return readSettings().orgchart?.customFieldDefinitions ?? [];
  }, [readSettings, shouldUseNormalizedFieldResolution]);

  // Helper: build NodeData for resolveNodeField
  const normalizedPersonNodeById = useMemo(
    (): Map<string, { attrs?: Record<string, unknown>; props?: Record<string, unknown> }> => {
      if (!shouldUseNormalizedFieldResolution) return new Map();
      const normalizedPersonNodes = buildOrgAssistantDiscoveryNodes({
        nodes,
        customFieldDefinitions,
        resolveCustomFields: resolveNodeCustomFields,
      });
      return new Map(normalizedPersonNodes.map((node) => [node.id, node] as const));
    },
    [customFieldDefinitions, nodes, shouldUseNormalizedFieldResolution],
  );

  const toNodeData = useCallback(
    (node: Node) => ({
      ...(node as unknown as Record<string, unknown>),
      id: node.id,
      name: node.name,
      attrs: {
        ...((normalizedPersonNodeById.get(node.id)?.attrs ?? node.attrs ?? {}) as Record<string, unknown>),
        ...(computedFieldsByNodeId.get(node.id) ?? {}),
      },
      props: {
        ...((normalizedPersonNodeById.get(node.id)?.props ?? node.props ?? {}) as Record<string, unknown>),
        ...(computedFieldsByNodeId.get(node.id) ?? {}),
      },
    }),
    [computedFieldsByNodeId, normalizedPersonNodeById],
  );


  // Pre-compute binding diff status per node (always-on visual indicator)
  type BindingDiffStatus = 'modified' | 'new';
  const bindingDiffMap = useMemo(() => {
    const map = new Map<string, BindingDiffStatus>();
    for (const node of nodes) {
      const binding = (node as unknown as Record<string, unknown>).dataBinding as ShapeDataBinding | undefined;
      if (!binding) continue;
      if (binding.isNewRecord) {
        map.set(node.id, 'new');
      } else if (binding.localChanges && Object.keys(binding.localChanges.fields).length > 0) {
        map.set(node.id, 'modified');
      }
    }
    return map;
  }, [nodes]);

  // Resolve a field value for heatmap/colorBy — uses resolveNodeField + structural fields
  const resolveField = useCallback((node: Node, field: string): unknown => {
    const structural = field.toLowerCase();
    if (structural === 'level' || structural === 'depth') {
      return structuralFields.levelMap.get(node.id) ?? null;
    }
    if (structural === 'directreports' || structural === 'direct_reports') {
      return structuralFields.directReportsMap.get(node.id) ?? null;
    }
    if (structural === 'totalreports' || structural === 'total_reports') {
      return structuralFields.totalReportsMap.get(node.id) ?? null;
    }
    return resolveOrgAnalyticValue(field, resolveNodeField(toNodeData(node), field));
  }, [structuralFields, toNodeData]);

  // Convert a resolved field value to a number (handles dates and numeric strings)
  const toNumeric = (raw: unknown): number => {
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      const num = parseFloat(trimmed);
      // If the entire string is a valid number, use it directly
      if (!isNaN(num) && trimmed === String(num)) return num;
      // Try parsing as a date (ISO strings, date-like values)
      const date = new Date(trimmed);
      if (!isNaN(date.getTime())) return date.getTime();
      // Partial numeric parse (e.g., "3.5 years")
      if (!isNaN(num)) return num;
    }
    return NaN;
  };

  const normalizeTenureYears = (raw: unknown): number => {
    const numeric = toNumeric(raw);
    if (isNaN(numeric)) return NaN;
    return Math.max(0, Math.floor(numeric));
  };

  const isTenureField = (fieldId: string): boolean => {
    const canonical = normalizeFieldId(fieldId);
    if (canonical === 'tenure') return true;
    const stripped = fieldId.toLowerCase().replace(/[^a-z0-9]/g, '');
    return stripped === 'tenure' || stripped === 'yearsofservice' || stripped === 'serviceyears';
  };

  // O(1) node lookup map — avoids O(n) nodes.find() in heatmap/colorBy closures
  const nodeById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);

  // A saved custom view bakes reporting-chain membership into the scene
  // itself (buildPeopleChartScene sets attrs.projectionContext), since it has
  // no interactive filter rules to derive matches from. Treat that the same
  // as an interactively-filtered chain node so a required manager can never
  // be mistaken for someone who matched.
  const isBakedReportingChainContext = useCallback((nodeId: string): boolean => {
    return isReportingChainContextNode(nodeById.get(nodeId));
  }, [nodeById]);

  const heatmapPaletteOverride = options?.lensPalette?.heatmap ?? null;
  const colorByPaletteOverride = options?.lensPalette?.colorBy ?? null;

  // Compute heatmap color for a node based on numeric field value
  const heatmapResult = useMemo(() => {
    if (!filterHeatmap) return { getColor: () => undefined as string | undefined, legend: null as HeatmapLegendData | null };
    const tenureHeatmap = isTenureField(filterHeatmap.field);
    const paletteRange = heatmapPaletteOverride?.field === filterHeatmap.field ? heatmapPaletteOverride : null;

    // Find min/max values for the field across ALL nodes (heatmap applies to all)
    let minValue = paletteRange?.min ?? filterHeatmap.minValue ?? Infinity;
    let maxValue = paletteRange?.max ?? filterHeatmap.maxValue ?? -Infinity;

    if (minValue === Infinity || maxValue === -Infinity) {
      for (const node of nodes) {
        const raw = resolveField(node, filterHeatmap.field);
        const value = tenureHeatmap ? normalizeTenureYears(raw) : toNumeric(raw);
        if (!isNaN(value)) {
          minValue = Math.min(minValue, value);
          maxValue = Math.max(maxValue, value);
        }
      }
    }

    const computedMin = minValue === Infinity ? 0 : minValue;
    const computedMax = maxValue === -Infinity ? 0 : maxValue;

    const getColor = (nodeId: string): string | undefined => {
      const node = nodeById.get(nodeId);
      if (!node) return undefined;

      const raw = resolveField(node, filterHeatmap.field);
      const value = tenureHeatmap ? normalizeTenureYears(raw) : toNumeric(raw);
      if (isNaN(value)) return undefined;

      if (computedMax === computedMin) return filterHeatmap.minColor;

      const ratio = Math.max(0, Math.min(1, (value - computedMin) / (computedMax - computedMin)));
      return interpolateColor(filterHeatmap.minColor, filterHeatmap.maxColor, ratio);
    };

    const legend: HeatmapLegendData = {
      field: filterHeatmap.field,
      minColor: filterHeatmap.minColor,
      maxColor: filterHeatmap.maxColor,
      minValue: computedMin,
      maxValue: computedMax,
    };

    return { getColor, legend };
  }, [filterHeatmap, heatmapPaletteOverride, nodes, nodeById, resolveField]);

  const computeHeatmapColor = heatmapResult.getColor;

  // Categorical palette — softer, modern tones for nominal fields
  const CATEGORY_COLORS = [
    '#60a5fa', '#f87171', '#4ade80', '#fbbf24', '#a78bfa',
    '#f472b6', '#22d3ee', '#a3e635', '#fb923c', '#818cf8',
  ];

  // Grouped sequential ramps for ordinal fields — ordered cool → warm so the
  // overall hue progression itself conveys low → high.  Within each ramp,
  // light → dark provides fine-grained distinction.
  const SEQUENTIAL_RAMPS: { min: string; max: string }[] = [
    { min: '#dbeafe', max: '#2563eb' }, // blue
    { min: '#ccfbf1', max: '#0d9488' }, // teal
    { min: '#fef3c7', max: '#d97706' }, // amber
    { min: '#ede9fe', max: '#7c3aed' }, // violet
    { min: '#ffe4e6', max: '#e11d48' }, // rose
  ];

  // Compute color by field for category-based coloring
  const colorByResult = useMemo(() => {
    const emptyColors = new Map<string, string>();
    if (!filterColorByField) return { getColor: () => undefined as string | undefined, legend: null as ColorByLegendEntry[] | null, colorsByKey: emptyColors };
    const paletteColors = colorByPaletteOverride?.field === filterColorByField.field
      ? colorByPaletteOverride.colorsByKey
      : null;
    const tenureColorBy = isTenureField(filterColorByField.field);
    const fieldCanonical = normalizeFieldId(filterColorByField.field);

    if (tenureColorBy || fieldCanonical === 'tenure') {
      const yearCountMap = new Map<number, number>();
      const nodeYearMap = new Map<string, number>();

      for (const node of nodes) {
        const raw = resolveField(node, filterColorByField.field);
        const year = normalizeTenureYears(raw);
        if (isNaN(year)) continue;
        nodeYearMap.set(node.id, year);
        yearCountMap.set(year, (yearCountMap.get(year) ?? 0) + 1);
      }

      const uniqueYears = Array.from(yearCountMap.keys()).sort((a, b) => a - b);
      if (uniqueYears.length === 0) {
        return { getColor: () => undefined as string | undefined, legend: null as ColorByLegendEntry[] | null, colorsByKey: emptyColors };
      }

      type TenureBucket = { min: number; max: number; label: string };
      const buckets: TenureBucket[] = [];
      const yearLabel = (year: number): string => (year === 1 ? '1 year' : `${year} years`);
      if (uniqueYears.length <= MAX_TENURE_COLOR_GROUPS) {
        for (const year of uniqueYears) {
          buckets.push({ min: year, max: year, label: yearLabel(year) });
        }
      } else {
        const minYear = uniqueYears[0];
        const maxYear = uniqueYears[uniqueYears.length - 1];
        const span = Math.max(1, maxYear - minYear + 1);
        const fibWeights = TENURE_BUCKET_WEIGHTS.slice(0, MAX_TENURE_COLOR_GROUPS);
        const totalWeight = fibWeights.reduce((sum, w) => sum + w, 0);
        let bucketStart = minYear;

        for (let i = 0; i < fibWeights.length; i++) {
          const isLast = i === fibWeights.length - 1;
          let bucketEnd: number;
          if (isLast) {
            bucketEnd = maxYear;
          } else {
            const cumulativeWeight = fibWeights.slice(0, i + 1).reduce((sum, w) => sum + w, 0);
            const projectedEnd = minYear + Math.max(0, Math.round((cumulativeWeight / totalWeight) * span) - 1);
            const minRemainingSlots = fibWeights.length - i - 1;
            bucketEnd = Math.min(maxYear - minRemainingSlots, Math.max(bucketStart, projectedEnd));
          }
          buckets.push({
            min: bucketStart,
            max: bucketEnd,
            label: bucketStart === bucketEnd ? yearLabel(bucketStart) : `${bucketStart}-${bucketEnd} years`,
          });
          bucketStart = bucketEnd + 1;
        }
      }

      const bucketColor = (idx: number, total: number): string => {
        if (total <= 1) return '#2563eb';
        const ratio = idx / (total - 1);
        return interpolateColor('#dbeafe', '#dc2626', ratio);
      };

      const yearToBucketIndex = new Map<number, number>();
      for (const year of uniqueYears) {
        const index = buckets.findIndex((bucket) => year >= bucket.min && year <= bucket.max);
        yearToBucketIndex.set(year, index >= 0 ? index : buckets.length - 1);
      }

      const legend = buckets
        .map((bucket, idx) => {
          let count = 0;
          for (const year of uniqueYears) {
            if (year >= bucket.min && year <= bucket.max) {
              count += yearCountMap.get(year) ?? 0;
            }
          }
          return {
            value: bucket.label,
            color: bucketColor(idx, buckets.length),
            count,
          };
        })
        .filter((entry) => entry.count > 0);

      const colorsByKey = new Map<string, string>();
      yearToBucketIndex.forEach((bucketIdx, year) => {
        colorsByKey.set(String(year), bucketColor(bucketIdx, buckets.length));
      });

      const getColor = (nodeId: string): string | undefined => {
        const year = nodeYearMap.get(nodeId);
        if (year == null) return undefined;
        if (paletteColors) return paletteColors.get(String(year));
        const bucketIdx = yearToBucketIndex.get(year);
        if (bucketIdx == null) return undefined;
        return bucketColor(bucketIdx, buckets.length);
      };

      return { getColor, legend, colorsByKey };
    }

    // First pass: collect unique values and counts
    const valueCountMap = new Map<string, number>();
    const insertionOrder: string[] = [];

    for (const node of nodes) {
      const value = resolveField(node, filterColorByField.field);
      const stringValue = value != null ? String(value) : '';

      if (stringValue) {
        if (!valueCountMap.has(stringValue)) {
          valueCountMap.set(stringValue, 0);
          insertionOrder.push(stringValue);
        }
        valueCountMap.set(stringValue, valueCountMap.get(stringValue)! + 1);
      }
    }

    // Detect ordinal: all values have an extractable numeric component
    const extractNumeric = (v: string): number | null => {
      const num = parseFloat(v);
      if (!isNaN(num)) return num;
      const match = v.match(/(\d+(?:\.\d+)?)/);
      return match ? parseFloat(match[1]) : null;
    };

    const uniqueValues = [...valueCountMap.keys()];
    const numericKeys = uniqueValues.map(extractNumeric);
    const configuredColors = filterColorByField.valueColors;
    const hasConfiguredColors = configuredColors != null && Object.keys(configuredColors).length > 0;
    const isOrdinal = !hasConfiguredColors
      && uniqueValues.length >= 2
      && numericKeys.every((n) => n !== null);

    // Assign colors
    const valueColorMap = new Map<string, string>();

    if (hasConfiguredColors) {
      let fallbackIndex = 0;
      for (const value of insertionOrder) {
        const configuredColor = fieldCanonical === 'successionRisk'
          ? resolveSuccessionRiskDisplayColor(value, configuredColors[value])
          : configuredColors[value];
        valueColorMap.set(
          value,
          configuredColor ?? CATEGORY_COLORS[fallbackIndex++ % CATEGORY_COLORS.length],
        );
      }
    } else if (isOrdinal) {
      // Sort by numeric component
      const sorted = uniqueValues
        .map((v, i) => ({ value: v, numKey: numericKeys[i]! }))
        .sort((a, b) => a.numKey - b.numKey);

      // Divide into groups (~3 per group), one hue ramp per group
      const numGroups = Math.min(SEQUENTIAL_RAMPS.length, Math.ceil(sorted.length / 3));
      const groupSize = Math.ceil(sorted.length / numGroups);

      for (let i = 0; i < sorted.length; i++) {
        const groupIdx = Math.min(Math.floor(i / groupSize), numGroups - 1);
        const ramp = SEQUENTIAL_RAMPS[groupIdx];
        const startInGroup = groupIdx * groupSize;
        const countInGroup = Math.min(groupSize, sorted.length - startInGroup);
        const posInGroup = i - startInGroup;
        const ratio = countInGroup === 1 ? 0.5 : posInGroup / (countInGroup - 1);
        valueColorMap.set(sorted[i].value, interpolateColor(ramp.min, ramp.max, ratio));
      }
    } else {
      // Nominal: assign distinct categorical colors
      let colorIndex = 0;
      for (const value of insertionOrder) {
        valueColorMap.set(value, CATEGORY_COLORS[colorIndex % CATEGORY_COLORS.length]);
        colorIndex++;
      }
    }

    const getColor = (nodeId: string): string | undefined => {
      const node = nodeById.get(nodeId);
      if (!node) return undefined;

      const value = resolveField(node, filterColorByField.field);
      const stringValue = value != null ? String(value) : '';

      return (paletteColors ?? valueColorMap).get(stringValue);
    };

    // Build legend in display order (sorted for ordinal, insertion order for nominal)
    const legendOrder = hasConfiguredColors
      ? [
          ...Object.keys(configuredColors).filter((value) => valueCountMap.has(value)),
          ...insertionOrder.filter((value) => configuredColors[value] == null),
        ]
      : isOrdinal
        ? uniqueValues
            .map((v, i) => ({ value: v, numKey: numericKeys[i]! }))
            .sort((a, b) => a.numKey - b.numKey)
            .map((e) => e.value)
        : insertionOrder;

    const legend: ColorByLegendEntry[] = legendOrder.map((value) => ({
      value,
      color: valueColorMap.get(value)!,
      count: valueCountMap.get(value) ?? 0,
    }));

    return { getColor, legend, colorsByKey: valueColorMap };
  }, [colorByPaletteOverride, filterColorByField, nodes, nodeById, resolveField]);

  const computeColorByField = colorByResult.getColor;

  const lensPalette = useMemo<LensPalette>(() => ({
    colorBy: filterColorByField
      ? { field: filterColorByField.field, colorsByKey: colorByResult.colorsByKey }
      : null,
    heatmap: filterHeatmap && heatmapResult.legend
      ? { field: filterHeatmap.field, min: heatmapResult.legend.minValue, max: heatmapResult.legend.maxValue }
      : null,
  }), [colorByResult.colorsByKey, filterColorByField, filterHeatmap, heatmapResult.legend]);

  // Get computed style for a node
  const getNodeStyle = useMemo(() => {
    return (nodeId: string): ComputedNodeStyle => {
      let filterOpacity = 1;
      let filterVisible = true;
      let backgroundColor: string | undefined;
      let borderColor: string | undefined;
      let textColor: string | undefined;
      const badges: ComputedBadge[] = [];

      const isMatching = matchingNodeIds.has(nodeId);
      // A saved custom view has no interactive filter to derive this from —
      // the scene itself already marked the node as required-manager context.
      const isBakedReportingChain = isBakedReportingChainContext(nodeId);

      // Apply view-all modes (heatmap, colorByField) to ALL nodes regardless of filter
      if (hasViewAllMode) {
        // Heatmap mode - color all nodes based on numeric field
        if (filterHeatmap) {
          const heatmapColor = computeHeatmapColor(nodeId);
          if (heatmapColor) {
            backgroundColor = heatmapColor;
          }
        }

        // Color by field mode - color all nodes based on category
        if (filterColorByField) {
          const categoryColor = computeColorByField(nodeId);
          if (categoryColor) {
            backgroundColor = categoryColor;
          }
        }
      }

      if (isFilterActive) {
        const isInReportingChain = isBakedReportingChain
          || (showReportingLines && reportingChainNodeIds.has(nodeId));

        if (isMatching) {
          filterOpacity = 1;
          filterVisible = true;

          // Apply highlight colors from primary filter to matching nodes
          if (filterHighlightColors) {
            backgroundColor = filterHighlightColors.backgroundColor;
            borderColor = filterHighlightColors.borderColor;
            textColor = filterHighlightColors.textColor;
          }

          // Apply badge decoration from primary filter
          if (filterBadge) {
            badges.push({
              icon: filterBadge.icon,
              color: filterBadge.color,
              position: filterBadge.position,
              tooltip: filterBadge.tooltip,
            });
          }

          // Apply heatmap color from primary filter (when combined with filter conditions)
          if (filterHeatmap && !hasViewAllMode) {
            const heatmapColor = computeHeatmapColor(nodeId);
            if (heatmapColor) {
              backgroundColor = heatmapColor;
            }
          }
        } else if (isInReportingChain) {
          filterOpacity = CHAIN_OPACITY;
          filterVisible = true;
          backgroundColor = CHAIN_BACKGROUND_COLOR;
          borderColor = CHAIN_BORDER_COLOR;
          textColor = CHAIN_TEXT_COLOR;
        } else {
          // Non-matching nodes: apply filter effect
          if (filterEffect === 'none') {
            // "Show" keeps non-matches visible and explicitly outlines them in blue.
            filterOpacity = 1;
            filterVisible = true;
            borderColor = '#2563EB';
          } else if (filterEffect === 'dim') {
            filterOpacity = DIM_OPACITY;
            filterVisible = true;
            // Override default accent border so stale/show-mode blue outlines do not persist.
            borderColor = CHAIN_BORDER_COLOR;
          } else {
            // hide
            filterOpacity = 0;
            filterVisible = false;
          }
        }
      } else if (isBakedReportingChain) {
        // No interactive filter is running, but the scene still needs the
        // required-manager context dimmed the same way — e.g. a saved
        // custom view, whose matches were computed at open time.
        filterOpacity = CHAIN_OPACITY;
        filterVisible = true;
        backgroundColor = CHAIN_BACKGROUND_COLOR;
        borderColor = CHAIN_BORDER_COLOR;
        textColor = CHAIN_TEXT_COLOR;
      }

      // Additional display rules can override primary filter colors
      const displayStyle = displayRuleStyleMap.get(nodeId);
      if (displayStyle) {
        if (displayStyle.backgroundColor) backgroundColor = displayStyle.backgroundColor;
        if (displayStyle.borderColor) borderColor = displayStyle.borderColor;
        if (displayStyle.textColor) textColor = displayStyle.textColor;
        badges.push(...displayStyle.badges);
      }

      // Reporting chain context nodes are intentionally neutral to avoid inheriting
      // stronger node theme hues in filtered department views, and display
      // rules can never make a baked-in context node appear matched.
      if (isBakedReportingChain
        || (isFilterActive && !isMatching && showReportingLines && reportingChainNodeIds.has(nodeId))) {
        backgroundColor = CHAIN_BACKGROUND_COLOR;
        borderColor = CHAIN_BORDER_COLOR;
        textColor = CHAIN_TEXT_COLOR;
      }

      return {
        filterOpacity,
        filterVisible,
        backgroundColor,
        borderColor,
        textColor,
        badges,
        labels: displayStyle?.labels ?? [],
      };
    };
  }, [isFilterActive, matchingNodeIds, filterEffect, showReportingLines, reportingChainNodeIds, filterHighlightColors, filterBadge, filterHeatmap, filterColorByField, hasViewAllMode, computeHeatmapColor, computeColorByField, displayRuleStyleMap, bindingDiffMap, isBakedReportingChainContext]);

  const isNodeVisible = useMemo(() => {
    return (nodeId: string): boolean => {
      if (!isFilterActive) return true;
      if (filterEffect === 'dim' || filterEffect === 'none') return true;
      if (matchingNodeIds.has(nodeId)) return true;
      if (showReportingLines && reportingChainNodeIds.has(nodeId)) return true;
      return false;
    };
  }, [isFilterActive, matchingNodeIds, filterEffect, showReportingLines, reportingChainNodeIds]);

  const getNodeOpacity = useMemo(() => {
    return (nodeId: string): number => {
      if (isBakedReportingChainContext(nodeId)) return CHAIN_OPACITY;
      if (!isFilterActive) return 1;
      if (matchingNodeIds.has(nodeId)) return 1;
      if (showReportingLines && reportingChainNodeIds.has(nodeId)) return CHAIN_OPACITY;
      // Non-matching nodes
      if (filterEffect === 'none') return 1;
      if (filterEffect === 'dim') return DIM_OPACITY;
      return 0; // hide
    };
  }, [isFilterActive, matchingNodeIds, filterEffect, showReportingLines, reportingChainNodeIds, isBakedReportingChainContext]);

  const isEdgeInReportingChain = useMemo(() => {
    return (edgeId: string): boolean => {
      return showReportingLines && reportingChainEdgeIds.has(edgeId);
    };
  }, [showReportingLines, reportingChainEdgeIds]);

  const getDisplayRuleStyle = useMemo(() => {
    return (nodeId: string): Pick<ComputedNodeStyle, 'backgroundColor' | 'borderColor' | 'textColor' | 'badges' | 'labels'> => {
      const isMatching = matchingNodeIds.has(nodeId);
      const style = displayRuleStyleMap.get(nodeId);
      const badges: ComputedBadge[] = [];

      // Start with styles from primary filter for matching nodes
      let backgroundColor: string | undefined;
      let borderColor: string | undefined;
      let textColor: string | undefined;

      // Apply view-all modes (heatmap, colorByField) to ALL nodes
      if (hasViewAllMode) {
        if (filterHeatmap) {
          const heatmapColor = computeHeatmapColor(nodeId);
          if (heatmapColor) {
            backgroundColor = heatmapColor;
          }
        }

        if (filterColorByField) {
          const categoryColor = computeColorByField(nodeId);
          if (categoryColor) {
            backgroundColor = categoryColor;
          }
        }
      }

      if (isFilterActive && isMatching) {
        // Apply highlight colors
        if (filterHighlightColors) {
          backgroundColor = filterHighlightColors.backgroundColor;
          borderColor = filterHighlightColors.borderColor;
          textColor = filterHighlightColors.textColor;
        }

        // Apply badge decoration
        if (filterBadge) {
          badges.push({
            icon: filterBadge.icon,
            color: filterBadge.color,
            position: filterBadge.position,
            tooltip: filterBadge.tooltip,
          });
        }

        // Apply heatmap color (when combined with filter conditions)
        if (filterHeatmap && !hasViewAllMode) {
          const heatmapColor = computeHeatmapColor(nodeId);
          if (heatmapColor) {
            backgroundColor = heatmapColor;
          }
        }
      }

      // Additional display rules can override
      if (style) {
        if (style.backgroundColor) backgroundColor = style.backgroundColor;
        if (style.borderColor) borderColor = style.borderColor;
        if (style.textColor) textColor = style.textColor;
        badges.push(...style.badges);
      }

      if (isFilterActive && !isMatching && showReportingLines && reportingChainNodeIds.has(nodeId)) {
        backgroundColor = CHAIN_BACKGROUND_COLOR;
        borderColor = CHAIN_BORDER_COLOR;
        textColor = CHAIN_TEXT_COLOR;
      }

      return {
        backgroundColor,
        borderColor,
        textColor,
        badges,
        labels: style?.labels ?? [],
      };
    };
  }, [isFilterActive, matchingNodeIds, filterHighlightColors, filterBadge, filterHeatmap, filterColorByField, hasViewAllMode, computeHeatmapColor, computeColorByField, displayRuleStyleMap, bindingDiffMap]);

  const getFieldColor = useMemo(() => {
    return (nodeId: string, fieldId: string): string | undefined => {
      const fieldCanonical = normalizeFieldId(fieldId);
      if (filterHeatmap && normalizeFieldId(filterHeatmap.field) === fieldCanonical) {
        return computeHeatmapColor(nodeId);
      }
      if (filterColorByField && normalizeFieldId(filterColorByField.field) === fieldCanonical) {
        return computeColorByField(nodeId);
      }
      return undefined;
    };
  }, [filterHeatmap, filterColorByField, computeHeatmapColor, computeColorByField]);

  // The orgchart-only early return used to live here; it short-circuited
  // every non-org diagram to empty defaults and was the reason network
  // lens tools (color_by, heatmap, badge, label, filter) landed in the
  // viewRules store but never painted the canvas. Removed in phase 7a so
  // the resolver runs for any domain whose nodes carry field-pack data.
  // Orgchart-specific extras (reporting chain, structural fields,
  // primary filter highlight colours) only kick in when their gating
  // rules exist, so they remain no-ops for non-org diagrams.

  return {
    nodeStyleMap,
    isFilterActive,
    filterMode: filterEffect,
    matchingCount: matchingNodeIds.size,
    matchingNodeIds,
    showReportingLines,
    reportingChainNodeIds,
    reportingChainEdgeIds,
    hasDisplayRules,
    getNodeStyle,
    isNodeVisible,
    getNodeOpacity,
    isEdgeInReportingChain,
    getDisplayRuleStyle,
    getFieldColor,
    activeHeatmapField: filterHeatmap?.field,
    activeColorByField: filterColorByField?.field,
    activeHeatmapDisplayName: heatmapRule?.name,
    activeColorByDisplayName: colorByRule?.name,
    colorByLegend: colorByResult.legend,
    heatmapLegend: heatmapResult.legend,
    lensPalette,
    bindingDiffCount: bindingDiffMap.size,
  };
};
